import React, { useState, useEffect, useCallback } from "react";
import { BarChart2, Users, Eye, TrendingUp, MousePointerClick, RefreshCw } from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from "recharts";
import api from "../../utils/api";

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-lg px-4 py-3 text-sm">
      <p className="font-bold text-slate-600 mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} style={{ color: p.color }} className="font-medium">
          {p.name}: <strong>{p.value.toLocaleString()}</strong>
        </p>
      ))}
    </div>
  );
};

export default function SiteTrafficWidget() {
  const [range, setRange] = useState("7d");
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const res = await api.get(`/api/analytics/admin/stats?range=${range}`);
      setStats(res.data);
      setError(null);
      setLastUpdated(new Date());
    } catch {
      setError("Could not load analytics data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [range]);

  // Loads on open and when the range changes; the Refresh button reloads — no timer
  useEffect(() => {
    load(false);
  }, [load]);


  const fmtDelta = (n) => (n >= 0 ? `+${n}%` : `${n}%`);
  const dClass = (n) => (n >= 0 ? "text-emerald-600" : "text-red-500");

  const kpis = stats
    ? [
        {
          label: "Visitors",
          value: stats.visitors.toLocaleString(),
          delta: fmtDelta(stats.deltaVisitors),
          dc: dClass(stats.deltaVisitors),
          icon: Users,
          color: "text-blue-600 bg-blue-50",
        },
        {
          label: "Page Views",
          value: stats.pageViews.toLocaleString(),
          delta: fmtDelta(stats.deltaPageViews),
          dc: dClass(stats.deltaPageViews),
          icon: Eye,
          color: "text-violet-600 bg-violet-50",
        },
        {
          label: "Avg. Session",
          value: stats.avgSession,
          delta: null,
          icon: TrendingUp,
          color: "text-emerald-600 bg-emerald-50",
        },
        {
          label: "Bounce Rate",
          value: `${stats.bounceRate}%`,
          delta: null,
          icon: MousePointerClick,
          color: "text-amber-600 bg-amber-50",
        },
      ]
    : [];

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h3 className="font-bold text-navy-900 flex items-center gap-2 flex-wrap">
            <BarChart2 size={18} className="text-blue-500" />
            Website Traffic
            <span className="text-[10px] font-mono text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
              tinyglobalop.com
            </span>
            {stats && (
              <span className="flex items-center gap-1.5 text-[11px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-100 px-2.5 py-0.5 rounded-full">
                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
                {stats.activeNow} active now
              </span>
            )}
          </h3>
          <p className="text-xs text-slate-400 mt-0.5">
            Real visitor data — live tracking
            {lastUpdated && (
              <span className="ml-2 text-slate-300">
                · updated {lastUpdated.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </span>
            )}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {/* Manual refresh */}
          <button
            onClick={() => load(true)}
            disabled={refreshing}
            title="Refresh"
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-100 hover:bg-slate-200 transition-colors disabled:opacity-40"
          >
            <RefreshCw size={13} className={refreshing ? "animate-spin text-slate-400" : "text-slate-500"} />
          </button>

          {/* Range toggle */}
          <div className="flex gap-1 bg-slate-100 p-1 rounded-xl">
            {["7d", "30d"].map((r) => (
              <button
                key={r}
                onClick={() => setRange(r)}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  range === r ? "bg-white text-navy-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                }`}
              >
                {r === "7d" ? "7 days" : "30 days"}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading && !stats ? (
        <div className="h-64 flex flex-col items-center justify-center gap-3 text-slate-400">
          <div className="w-8 h-8 border-2 border-slate-200 border-t-blue-500 rounded-full animate-spin" />
          <p className="text-xs">Loading analytics…</p>
        </div>
      ) : error ? (
        <div className="h-64 flex flex-col items-center justify-center gap-2 text-slate-400">
          <p className="text-sm text-red-400">{error}</p>
          <button
            onClick={() => load(false)}
            className="text-xs text-blue-500 hover:underline"
          >
            Retry
          </button>
        </div>
      ) : (
        <>
          {/* KPI row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-y lg:divide-y-0 divide-slate-100">
            {kpis.map(({ label, value, delta, dc, icon: Icon, color }) => (
              <div key={label} className="px-6 py-4 flex items-center gap-3">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${color}`}>
                  <Icon size={16} />
                </div>
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{label}</p>
                  <p className="font-bold text-navy-900 text-lg leading-tight">{value}</p>
                  {delta && (
                    <p className={`text-[10px] font-bold ${dc}`}>
                      {delta} vs prev period
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Chart */}
          <div className="px-6 pb-6 pt-4">
            {stats.chartData?.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={stats.chartData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gVisitors" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.15} />
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="gViews" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.12} />
                        <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis
                      dataKey="day"
                      tick={{ fontSize: 11, fill: "#94a3b8", fontWeight: 600 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area
                      type="monotone"
                      dataKey="pageViews"
                      name="Page Views"
                      stroke="#8b5cf6"
                      strokeWidth={2}
                      fill="url(#gViews)"
                      dot={false}
                      activeDot={{ r: 4, fill: "#8b5cf6" }}
                    />
                    <Area
                      type="monotone"
                      dataKey="visitors"
                      name="Visitors"
                      stroke="#3b82f6"
                      strokeWidth={2}
                      fill="url(#gVisitors)"
                      dot={false}
                      activeDot={{ r: 4, fill: "#3b82f6" }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
                <div className="flex items-center gap-5 mt-2 justify-center">
                  <span className="flex items-center gap-1.5 text-xs font-medium text-slate-500">
                    <span className="w-3 h-0.5 bg-blue-500 rounded-full inline-block" /> Visitors
                  </span>
                  <span className="flex items-center gap-1.5 text-xs font-medium text-slate-500">
                    <span className="w-3 h-0.5 bg-violet-500 rounded-full inline-block" /> Page Views
                  </span>
                </div>
              </>
            ) : (
              <div className="h-[220px] flex items-center justify-center text-xs text-slate-400">
                No traffic data yet for this period. The chart will populate as visitors arrive.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
